import React, { useState, useEffect, useCallback, type ReactNode } from "react";
import { resetAnalytics } from "../analytics";
import api, {
  clearAuthCallbackAutoRecoveryAttempt,
  clearForceLoginPromptAfterLogout,
  clearLocalAuthState,
  getViewAsState,
  hydrateAdminChatbotIds,
  persistAuthSessionState,
  selfHealStaleViewAsState,
} from "../utils/api";
import type { AuthUser, Membership, UserSettings } from "../types";
import {
  createTimeoutAwareClient,
  getRequestConfig,
  normalizeMemberships,
  type AuthRequestOptions,
} from "./authContextSupport";
import type { AuthContextValue } from "./authContextTypes";
import { AuthContext, useAuth } from "./authContextCore";
import { useAuthPermissions } from "./useAuthPermissions";
import { useOrganizationAccess } from "./useOrganizationAccess";
import { useAuthSessionBootstrap } from "./useAuthBootstrap";

export type { OrganizationAccessStatus } from "./authContextSupport";
export type { AuthContextValue } from "./authContextTypes";
export { useAuth };

export function AuthProvider({ children }: { children?: ReactNode }) {
  const [authError, setAuthError] = useState("");
  const [authErrorTitle, setAuthErrorTitle] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const {
    manageableOrganizationsCount,
    organizationAccessStatus,
    organizationAccessStatusRef,
    resetOrganizationAccessState,
    markOrganizationAccessDenied,
    refreshManageableOrganizations,
  } = useOrganizationAccess();
  const isAdmin = Boolean(userSettings?.isAdmin ?? user?.isAdmin);
  const staffTeam = userSettings?.staffTeam ?? null;
  const canManageOrganizations =
    organizationAccessStatus !== "denied" && (isAdmin || manageableOrganizationsCount > 0);
  const hasOrganizationContext = Boolean(userSettings?.activeOrganization?.organizationId);

  const clearResolvedAuthError = useCallback(() => {
    setAuthError("");
    setAuthErrorTitle("");
  }, []);

  const syncSettings = useCallback(
    (settings: UserSettings | null) => {
      if (getViewAsState() !== null && !settings?.viewAs) {
        selfHealStaleViewAsState();
        return;
      }

      const nextMemberships = normalizeMemberships(settings);

      clearResolvedAuthError();
      clearAuthCallbackAutoRecoveryAttempt();
      setUserSettings(settings);
      setMemberships(nextMemberships);
      persistAuthSessionState(settings);
      clearForceLoginPromptAfterLogout();

      setUser((prev) => ({
        ...(prev || {}),
        isAdmin: settings?.isAdmin === true,
        memberships: nextMemberships,
        organizations: settings?.organizations || [],
        role: settings?.role || null,
      }));
    },
    [clearResolvedAuthError],
  );

  const clearResolvedAuthState = useCallback(() => {
    clearLocalAuthState();
    clearResolvedAuthError();
    setIsAuthenticated(false);
    setUser(null);
    resetOrganizationAccessState();
    setUserSettings(null);
    setMemberships([]);
    resetAnalytics();
  }, [clearResolvedAuthError, resetOrganizationAccessState]);

  const fetchUserSettings = useCallback(
    async ({ requestTimeoutMs, skipAuthRecovery = false }: AuthRequestOptions = {}) => {
      const response = await api.get(
        "/api/v2/auth/me",
        getRequestConfig({ skipAuthRecovery }, requestTimeoutMs),
      );

      if (response.status === 200) {
        if (skipAuthRecovery !== true && organizationAccessStatusRef.current !== "denied") {
          resetOrganizationAccessState();
        }

        const hydratedSettings = await hydrateAdminChatbotIds(
          response.data,
          createTimeoutAwareClient(requestTimeoutMs, skipAuthRecovery),
        );
        syncSettings(hydratedSettings);
        await refreshManageableOrganizations({
          requestTimeoutMs,
          suppressErrors: true,
          skipAuthRecovery,
        });
      }
    },
    [refreshManageableOrganizations, resetOrganizationAccessState, syncSettings],
  );

  const { handleLoginSuccess, handleLogout } = useAuthSessionBootstrap({
    clearResolvedAuthError,
    clearResolvedAuthState,
    fetchUserSettings,
    isAuthenticated,
    setAuthError,
    setAuthErrorTitle,
    setIsAuthenticated,
    setIsLoading,
    setUser,
  });

  const { hasOrgAccess, hasPermission } = useAuthPermissions({
    canManageOrganizations,
    memberships,
    user,
  });

  const value: AuthContextValue = {
    authError,
    authErrorTitle,
    canManageOrganizations,
    fetchUserSettings,
    handleLoginSuccess,
    handleLogout,
    hasOrgAccess,
    hasOrganizationContext,
    hasPermission,
    isAdmin,
    staffTeam,
    isAuthenticated,
    isLoading,
    markOrganizationAccessDenied,
    manageableOrganizationsCount,
    memberships,
    organizationAccessStatus,
    refreshManageableOrganizations,
    user,
    userSettings,
    viewAs: userSettings?.viewAs ?? null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
